import { AudioManager } from "@/phaser/core/audio/AudioManager";
import { BaseButton } from "@phaser/ui/btns/BaseButton";

export type ArrowDirection = "LEFT" | "RIGHT";

interface ArrowBtnOptions {
  textureKey: string;
  direction: ArrowDirection;
  onClick: () => void;
}

export class ArrowBtn extends BaseButton {
  private bg: Phaser.GameObjects.Sprite;
  private effect: Phaser.GameObjects.Sprite;

  private readonly textureKey: string;
  private readonly direction: ArrowDirection;
  private readonly defaultFrame: number;
  private readonly effectAnimKey: string;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    options: ArrowBtnOptions,
  ) {
    super(scene, x, y, {
      onClick: options.onClick,
      sfx: () => AudioManager.playSfx("sfx-swoosh"),
      onPress: () => this.setPressedStyle(),
      onRelease: () => this.resetButton(),
    });

    this.textureKey = options.textureKey;
    this.direction = options.direction;
    this.defaultFrame = options.direction === "LEFT" ? 0 : 1;
    this.effectAnimKey = `${this.textureKey}-arrow-click-effect`;

    this.createEffectAnim(scene);

    this.bg = scene.add
      .sprite(0, 0, this.textureKey, this.defaultFrame)
      .setOrigin(0.5);

    this.effect = scene.add
      .sprite(0, 0, this.textureKey, 4)
      .setOrigin(0.5)
      .setFlipX(this.direction === "RIGHT")
      .setVisible(false);

    this.add([this.bg, this.effect]);

    this.enableButton(this.bg.width, this.bg.height);

    scene.add.existing(this);
  }

  private createEffectAnim(scene: Phaser.Scene) {
    if (scene.anims.exists(this.effectAnimKey)) return;

    scene.anims.create({
      key: this.effectAnimKey,
      frames: scene.anims.generateFrameNumbers(this.textureKey, {
        start: 4,
        end: 7,
      }),
      frameRate: 16,
      repeat: 0,
    });
  }

  private setPressedStyle() {
    this.setScale(0.9);

    this.effect
      .setVisible(true)
      .setFrame(4)
      .setScale(2)
      .setFlipX(this.direction === "RIGHT");

    this.scene.tweens.add({
      targets: this.effect,
      scale: 1.5,
      duration: 120,
      ease: "Back.Out",
    });

    this.effect.play(this.effectAnimKey);

    this.effect.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      this.effect.setVisible(false);
    });
  }

  private resetButton() {
    this.setScale(1);
    this.bg.setFrame(this.defaultFrame);
  }
}
